import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  Check,
  FlashOn,
  Home,
  RocketLaunch,
  Close,
  ExpandMore,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

const Pricing = () => {
  const theme = useTheme();

  // Initialize all FAQs as expanded
  const [collapsedFAQs, setCollapsedFAQs] = useState(new Set());

  const handleFAQToggle = (index) => {
    setCollapsedFAQs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const plans = [
    {
      name: "Preview",
      price: "Free",
      description:
        "Default plan for the current preview version users. We will end preview access later.",
      features: [
        "Full features",
        "Unlimited automations",
        "100 tokens per month",
      ],
      restrictions: [],
      cta: "Get Started",
      popular: false,
      href: "https://app.mhome.ai",
    },
    {
      name: "Free",
      price: "Free",
      description:
        "Basic plan for the light users. Access to most popular integrations.",
      features: ["Full features", "Max 20 automations", "20 tokens per month"],
      restrictions: [
        "Limited automations",
        "Limited tokens",
        "Some integrations are restricted",
      ],
      cta: "Get Started",
      popular: false,
      href: "https://app.mhome.ai",
    },
    {
      name: "Pro",
      price: "$4.99",
      period: "/month",
      description:
        "Unlimitted automations. Broad integrations access. Choice for most users.",
      features: [
        "Full features",
        "Unlimited automations",
        "300 tokens per month",
        "Access to almost all integrations",
      ],
      restrictions: [],
      cta: "Get Started",
      popular: false,
      href: "https://app.mhome.ai",
    },
    {
      name: "Max",
      price: "TBD",
      period: "",
      description: "Unlimited tokens usgae. Access to all integrations.",
      features: [
        "Full features",
        "Unlimited automations",
        "Unlimited tokens usgae",
        "Access to all integrations",
      ],
      restrictions: [],
      cta: "Get Started",
      popular: false,
      href: "https://app.mhome.ai",
    },
  ];

  // Split plans into preview and standard plans
  const previewPlan = plans[0];
  const standardPlans = plans.slice(1);

  const faqs = [
    {
      question: "Can I use all the advanced features for free?",
      answer:
        "Yes, every user gets full access to all the features. The subscription plan only affects the AI tokens, and automation limits.",
    },
    {
      question: "What is a 'token'?",
      answer: `Token is the unit of AI usage. One chat request costs one 'token'. 
      Some queries may cost less than one token (or even not cost any), like non-AI involved commands, or cache-hits.
      You can check the token expense in the app.`,
    },
    {
      question: "Can I change plans anytime?",
      answer:
        "Yes, you can upgrade or downgrade your plan at any time. Upgrade takes effect immediately. Downgrade will take effect at the end of the current billing period.",
    },
    {
      question: "Is there a free trial?",
      answer:
        "Currently no, but you can explore almost everything with our free plan.",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.palette.background.gradient,
        pt: { xs: 12, md: 16 },
        pb: { xs: 8, md: 12 },
      }}
    >
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "4rem", lg: "5rem" },
              fontWeight: "bold",
              color: theme.palette.text.primary,
              mb: 4,
              lineHeight: 1.1,
            }}
          >
            <Box
              component="span"
              sx={{
                background: theme.palette.brand.gradientText,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                WebkitTextFillColor: "transparent",
              }}
            >
              Simple
            </Box>{" "}
            Pricing
          </Typography>
        </Box>
      </Container>

      {/* Pricing Cards */}
      <Container maxWidth="lg" sx={{ mb: 12 }}>
        {/* Preview Plan - Takes its own row */}
        <Grid
          container
          spacing={{ xs: 2, md: 4 }}
          sx={{ maxWidth: "6xl", mx: "auto", mb: 4 }}
        >
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
            <Box sx={{ maxWidth: "400px", width: "100%" }}>
              <Card
                sx={{
                  position: "relative",
                  height: "100%",
                  backgroundColor: theme.palette.background.card,
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${theme.palette.border.light}`,
                  borderRadius: 3,
                  transition: `all ${theme.transitions.duration.standard}ms ease`,
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: theme.shadows[12],
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
                  {/* Plan Name */}
                  <Typography
                    variant="h4"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: "bold",
                      mb: 1,
                      fontSize: { xs: "1.5rem", md: "2rem" },
                    }}
                  >
                    {previewPlan.name}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.quaternary,
                      mb: { xs: 3, md: 4 },
                      fontSize: { xs: "0.875rem", md: "1rem" },
                      lineHeight: 1.5,
                      minHeight: { xs: "60px", md: "72px" },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    {previewPlan.description}
                  </Typography>

                  {/* Price */}
                  <Box sx={{ mb: { xs: 3, md: 4 } }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { xs: "2rem", md: "3rem" },
                        fontWeight: "bold",
                        color: theme.palette.text.primary,
                        display: "inline",
                      }}
                    >
                      {previewPlan.price}
                    </Typography>
                    {previewPlan.period && (
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.text.quaternary,
                          display: "inline",
                          ml: 1,
                          fontSize: { xs: "1rem", md: "1.25rem" },
                        }}
                      >
                        {previewPlan.period}
                      </Typography>
                    )}
                  </Box>

                  {/* Features */}
                  <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: "left" }}>
                    {previewPlan.features.map((feature, featureIndex) => (
                      <Box
                        key={featureIndex}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: { xs: 1.5, md: 2 },
                        }}
                      >
                        <Check
                          sx={{
                            fontSize: { xs: 18, md: 20 },
                            color: theme.palette.status.available,
                            mr: 2,
                            flexShrink: 0,
                            mt: 0.25,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: { xs: "0.875rem", md: "1rem" },
                            lineHeight: 1.4,
                          }}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}

                    {/* Restrictions - shown in same list style */}
                    {previewPlan.restrictions.map(
                      (restriction, restrictionIndex) => (
                        <Box
                          key={`restriction-${restrictionIndex}`}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            mb: { xs: 1.5, md: 2 },
                          }}
                        >
                          <Close
                            sx={{
                              fontSize: { xs: 18, md: 20 },
                              color: theme.palette.status.restricted,
                              mr: 2,
                              flexShrink: 0,
                              mt: 0.25,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.tertiary,
                              fontSize: { xs: "0.875rem", md: "1rem" },
                              lineHeight: 1.4,
                            }}
                          >
                            {restriction}
                          </Typography>
                        </Box>
                      ),
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>

        {/* Standard Plans - Next row */}
        <Grid
          container
          spacing={{ xs: 2, md: 4 }}
          sx={{ maxWidth: "6xl", mx: "auto" }}
        >
          {standardPlans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  position: "relative",
                  height: "100%",
                  backgroundColor: theme.palette.background.card,
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${theme.palette.border.light}`,
                  borderRadius: 3,
                  transition: `all ${theme.transitions.duration.standard}ms ease`,
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: theme.shadows[12],
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
                  {/* Plan Name */}
                  <Typography
                    variant="h4"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: "bold",
                      mb: 1,
                      fontSize: { xs: "1.5rem", md: "2rem" },
                    }}
                  >
                    {plan.name}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.quaternary,
                      mb: { xs: 3, md: 4 },
                      fontSize: { xs: "0.875rem", md: "1rem" },
                      lineHeight: 1.5,
                      minHeight: { xs: "60px", md: "72px" },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    {plan.description}
                  </Typography>

                  {/* Price */}
                  <Box sx={{ mb: { xs: 3, md: 4 } }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { xs: "2rem", md: "3rem" },
                        fontWeight: "bold",
                        color: theme.palette.text.primary,
                        display: "inline",
                      }}
                    >
                      {plan.price}
                    </Typography>
                    {plan.period && (
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.text.quaternary,
                          display: "inline",
                          ml: 1,
                          fontSize: { xs: "1rem", md: "1.25rem" },
                        }}
                      >
                        {plan.period}
                      </Typography>
                    )}
                  </Box>

                  {/* Features */}
                  <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: "left" }}>
                    {plan.features.map((feature, featureIndex) => (
                      <Box
                        key={featureIndex}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: { xs: 1.5, md: 2 },
                        }}
                      >
                        <Check
                          sx={{
                            fontSize: { xs: 18, md: 20 },
                            color: theme.palette.status.available,
                            mr: 2,
                            flexShrink: 0,
                            mt: 0.25,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: { xs: "0.875rem", md: "1rem" },
                            lineHeight: 1.4,
                          }}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}

                    {/* Restrictions - shown in same list style */}
                    {plan.restrictions.map((restriction, restrictionIndex) => (
                      <Box
                        key={`restriction-${restrictionIndex}`}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: { xs: 1.5, md: 2 },
                        }}
                      >
                        <Close
                          sx={{
                            fontSize: { xs: 18, md: 20 },
                            color: theme.palette.status.restricted,
                            mr: 2,
                            flexShrink: 0,
                            mt: 0.25,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: { xs: "0.875rem", md: "1rem" },
                            lineHeight: 1.4,
                          }}
                        >
                          {restriction}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ Section */}
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.75rem", md: "3rem" },
              fontWeight: "bold",
              color: theme.palette.text.primary,
              mb: 3,
            }}
          >
            Frequently Asked Questions
          </Typography>
        </Box>

        <Box sx={{ maxWidth: "4xl", mx: "auto" }}>
          {faqs.map((faq, index) => (
            <Card
              key={index}
              sx={{
                mb: 2,
                backgroundColor: theme.palette.background.card,
                backdropFilter: "blur(10px)",
                border: `1px solid ${theme.palette.border.light}`,
                borderRadius: 2,
                overflow: "hidden",
                cursor: "pointer",
                transition: `all ${theme.transitions.duration.standard}ms ease`,
                "&:hover": {
                  boxShadow: theme.shadows[8],
                },
              }}
              onClick={() => handleFAQToggle(index)}
            >
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: { xs: 2, md: 3 },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: theme.palette.text.primary,
                      fontSize: { xs: "1rem", md: "1.125rem" },
                      flex: 1,
                    }}
                  >
                    {faq.question}
                  </Typography>
                  <IconButton
                    sx={{
                      transform: !collapsedFAQs.has(index)
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: `transform ${theme.transitions.duration.standard}ms ease`,
                      color: theme.palette.text.primary,
                    }}
                  >
                    <ExpandMore />
                  </IconButton>
                </Box>
                <Collapse
                  in={!collapsedFAQs.has(index)}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.quaternary,
                        lineHeight: 1.5,
                        fontSize: { xs: "0.875rem", md: "0.95rem" },
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Pricing;
